import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { CheckSquare, Target, Clock, ShieldCheck, Plus, CalendarDays, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePanel } from "@/context/PanelContext";
import { formatHM, timeAgo, formatTime } from "@/lib/utils-tg";

function Stat({ icon: Icon, label, value, sub, color, to }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => to && navigate(to)}
      className="tg-card p-5 text-left w-full hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <div className="flex items-start gap-3">
        <div className={`tg-stat-icon ${color.bg}`}>
          <Icon className={`h-5 w-5 ${color.text}`} />
        </div>
        <div className="flex-1">
          <div className="text-sm text-slate-500">{label}</div>
          <div className="font-display text-3xl font-bold text-slate-900 leading-tight mt-0.5">{value}</div>
          <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
        </div>
      </div>
    </button>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const { openNew } = useOutletContext();
  const { open: openPanel, refreshKey } = usePanel();
  const [stats, setStats] = useState(null);
  const [home, setHome] = useState({ plan: [], activity: [] });

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/home/today")]).then(([s, h]) => {
      setStats(s.data);
      setHome(h.data);
    });
  }, [refreshKey]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  const openItem = async (kind, id) => {
    if (kind === "task") {
      const r = await api.get("/tasks");
      const item = r.data.find((x) => x.id === id);
      if (item) openPanel("task", item);
    } else if (kind === "meeting") {
      const r = await api.get(`/meetings/${id}`);
      openPanel("meeting", r.data);
    }
  };

  return (
    <div className="px-6 md:px-10 pt-6 max-w-6xl mx-auto" data-testid="home-page">
      <div className="mb-1">
        <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
          {greeting}, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Here's what's happening today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat
          icon={CheckSquare}
          label="Tasks"
          value={stats?.tasks ?? "—"}
          sub="In progress"
          color={{ bg: "bg-blue-50", text: "text-blue-600" }}
          to="/tasks"
        />
        <Stat
          icon={Target}
          label="Goals"
          value={stats?.goals ?? "—"}
          sub="Active this sprint"
          color={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
          to="/goals"
        />
        <Stat
          icon={Clock}
          label="Timesheet"
          value={stats ? formatHM(stats.timesheet_minutes_today) : "—"}
          sub="Logged today"
          color={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
          to="/timesheet"
        />
        <Stat
          icon={ShieldCheck}
          label="Approvals"
          value={stats?.approvals_pending ?? "—"}
          sub="Pending"
          color={{ bg: "bg-orange-50", text: "text-orange-600" }}
          to="/approvals"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <section className="tg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold text-slate-900">Today's Plan</h2>
            <button onClick={openNew} className="tg-btn-ghost" data-testid="add-plan-item">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {home.plan.length === 0 && (
              <li className="text-sm text-slate-400 py-4">Nothing planned yet. Add a task or meeting.</li>
            )}
            {home.plan.map((p, i) => (
              <li
                key={`${p.kind}-${p.id}`}
                className="py-3 flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 rounded-xl cursor-pointer transition-colors"
                onClick={() => openItem(p.kind, p.id)}
                data-testid={`plan-item-${i}`}
              >
                <div
                  className={`tg-stat-icon h-9 w-9 ${
                    p.kind === "meeting" ? "bg-emerald-50" : "bg-blue-50"
                  }`}
                >
                  {p.kind === "meeting" ? (
                    <CalendarDays className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">
                    {p.kind === "meeting" ? formatTime(p.time) : p.time || "Today"}
                  </div>
                </div>
                <span
                  className={`tg-pill ${
                    p.kind === "meeting"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-blue-50 text-blue-700 border-blue-200"
                  }`}
                >
                  {p.kind}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="tg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold text-slate-900">Recent Activity</h2>
          </div>
          <ul className="divide-y divide-slate-100">
            {home.activity.length === 0 && (
              <li className="text-sm text-slate-400 py-4">No activity yet today.</li>
            )}
            {home.activity.map((a) => (
              <li key={a.id} className="py-3 flex items-center gap-3" data-testid="activity-item">
                <div className="tg-stat-icon h-9 w-9 bg-emerald-50">
                  <Clock className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">
                    Logged {formatHM(a.minutes)} on {a.title}
                  </div>
                  <div className="text-xs text-slate-500">{timeAgo(a.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
